/**
 * AppLayout — sidebar + topbar v12 + content area.
 *
 * Topbar layout (left to right):
 *   [hamburger — mobile only, opens nav drawer]
 *   [search bar (centered, ~480px)] [⌘K chip]
 *   [EN/RO toggle] [BALANCE display] [bell] [help] [avatar ↓]
 *
 * The avatar opens a dropdown showing the org name + user email + a single
 * "Sign out" action. Logout used to be a standalone button — it now lives
 * in this menu (more space for the topbar's signal items).
 *
 * Search, balance, bell, help are visible-only for now. They wire up in
 * later phases (Phase 4.6 search/cmd-palette, 4.7 balance API, 4.4 inbox,
 * 4.8 help index).
 *
 * Responsive (Pas 1b): below md the desktop <aside> is hidden, so a hamburger
 * in the Topbar opens MobileNavDrawer — an overlay that renders the SAME
 * <SidebarNav> (single source of truth) in a slide-in panel. Tapping a link or
 * the backdrop, or pressing Escape, closes it. Desktop (md+) is unaffected: the
 * hamburger is md:hidden and the drawer never mounts visibly.
 */
import { useState, useRef, useEffect, Suspense } from "react";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Outlet, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import GlobalSearch from "@/components/GlobalSearch";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, HelpCircle, ChevronDown, LogOut, Menu, X } from "lucide-react";

import { api } from "@/lib/api";
import { useBalance } from "@/lib/api/balance";
import { useAuth, meQueryKey } from "@/lib/auth/context";
import Sidebar, { SidebarNav } from "@/components/Sidebar";
import { PageLoadingFallback } from "@/components/PageLoadingFallback";
import SupportAccessBanner from "@/components/SupportAccessBanner";
import { ToastViewport } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

export default function AppLayout() {
  const { t } = useTranslation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-surface-base">
      <Sidebar />

      <MobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        t={t}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar t={t} onMenuClick={() => setMobileNavOpen(true)} />

        <ImpersonationBanner />
        <SupportAccessBanner />

        <main className="flex-1 px-6 py-8 animate-fade-in md:px-10">
          <div className="mx-auto max-w-6xl">
            <Suspense fallback={<PageLoadingFallback />}>
              <SuspendedBanner />
              <ScrollToTop />
        <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
      <ToastViewport />
    </div>
  );
}

// =================================================================
// Mobile nav drawer (below md)
// =================================================================

function MobileNavDrawer({
  open,
  onClose,
  t,
}: {
  open: boolean;
  onClose: () => void;
  t: (key: string) => string;
}) {
  // Close on Escape + lock body scroll while open. Mirrors the AvatarMenu
  // pattern (keydown listener), scoped to when the drawer is actually open.
  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleEscape);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
      {/* Backdrop — tap to close */}
      <button
        type="button"
        aria-label={t("topbar.close_menu")}
        onClick={onClose}
        className="absolute inset-0 h-full w-full bg-ink/40 backdrop-blur-sm animate-fade-in"
      />

      {/* Panel — slides in from the left, hosts the shared SidebarNav */}
      <div className="absolute left-0 top-0 flex h-full w-[260px] max-w-[82vw] flex-col border-r border-border bg-surface-card shadow-2xl animate-fade-in">
        <div className="flex justify-end px-3 pt-3">
          <button
            type="button"
            onClick={onClose}
            aria-label={t("topbar.close_menu")}
            className="flex h-9 w-9 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface-subtle hover:text-ink focus:outline-none focus-visible:shadow-focus"
          >
            <X className="h-5 w-5 stroke-[1.75]" aria-hidden="true" />
          </button>
        </div>
        <SidebarNav onNavigate={onClose} />
      </div>
    </div>
  );
}

// =================================================================
// Topbar
// =================================================================

function Topbar({
  t,
  onMenuClick,
}: {
  t: (key: string) => string;
  onMenuClick: () => void;
}) {
  return (
    <header className="sticky top-0 z-10 border-b border-border bg-surface-card/80 backdrop-blur-md">
      <div className="flex h-16 items-center gap-4 px-6">
        {/* Hamburger — mobile only, opens the nav drawer */}
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={t("topbar.open_menu")}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface-subtle hover:text-ink focus:outline-none focus-visible:shadow-focus md:hidden"
        >
          <Menu className="h-5 w-5 stroke-[1.75]" aria-hidden="true" />
        </button>

        {/* Search bar — centered with flex-grow */}
        <div className="flex flex-1 justify-center">
          <SearchBar />
        </div>

        {/* Right cluster */}
        <div className="flex shrink-0 items-center gap-2">
          <LangToggle />
          <BalanceDisplay />
          <IconButton
            icon={Bell}
            label={t("topbar.notifications")}
            onClick={() => {
              /* Phase 4.4: open notifications panel */
            }}
          />
          <IconButton
            icon={HelpCircle}
            label={t("topbar.help")}
            onClick={() => {
              /* Phase 4.8: navigate to /help */
            }}
          />
          <AvatarMenu />
        </div>
      </div>
    </header>
  );
}

// =================================================================
// Search bar
// =================================================================

function SearchBar() {
  return <GlobalSearch />;
}

// =================================================================
// Language toggle
// =================================================================

function LangToggle() {
  const { i18n } = useTranslation();
  const current = i18n.resolvedLanguage === "ro" ? "ro" : "en";

  return (
    <div
      className="inline-flex h-8 items-center rounded-pill bg-surface-subtle p-0.5"
      role="group"
      aria-label="Language"
    >
      <button
        type="button"
        onClick={() => i18n.changeLanguage("en")}
        className={cn(
          "h-7 rounded-pill px-3 text-micro font-semibold uppercase tracking-wider transition-colors",
          current === "en"
            ? "bg-ink text-surface-card"
            : "text-ink-muted hover:text-ink",
        )}
        aria-pressed={current === "en"}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => i18n.changeLanguage("ro")}
        className={cn(
          "h-7 rounded-pill px-3 text-micro font-semibold uppercase tracking-wider transition-colors",
          current === "ro"
            ? "bg-ink text-surface-card"
            : "text-ink-muted hover:text-ink",
        )}
        aria-pressed={current === "ro"}
      >
        RO
      </button>
    </div>
  );
}

// =================================================================
// Suspension banner (D-SUSPEND-UX): unpaid invoices -> in-your-face,
// on EVERY page (shell-level). Same useBalance query = zero new fetch.
// =================================================================

function SuspendedBanner() {
  const { i18n } = useTranslation();
  const { data } = useBalance();
  if (!data || data.billing_status !== "suspended_billing") return null;
  // Bilingual inline on the CURRENT UI language (minimal-touch; dedicated
  // locale keys remain the tracked i18n debt).
  const ro = (i18n.language ?? "ro").toLowerCase().startsWith("ro");
  return (
    <div className="mx-4 mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-2xl bg-red-600/95 px-4 py-2.5 text-center text-[13px] font-semibold text-white shadow-sm ring-1 ring-red-700/30 backdrop-blur-sm sm:mx-6">
      <span aria-hidden="true">&#9888;</span>
      <span>{ro ? "Cont suspendat pentru facturi neachitate \u2014 trimiterile sunt oprite pe toate canalele." : "Account suspended for unpaid invoices \u2014 sending is disabled on all channels."}</span>
      <a href="/invoices" className="shrink-0 underline decoration-white/60 underline-offset-2 hover:decoration-white">{ro ? "Vezi facturile" : "View invoices"}</a>
    </div>
  );
}

// =================================================================
// Balance display
// =================================================================

function BalanceDisplay() {
  const { t } = useTranslation();

  // Phase 4.7 LIVE: useBalance() -> GET /api/sendsms-credentials/balance
  // (KV 300s server-side, V370; staleTime oglindit in hook). Fail-closed
  // pe "—" la orice eroare (cont nelegat, upstream down).
  const { data } = useBalance();
  const balance =
    data && typeof data.balance === "number"
      ? data.balance.toFixed(3)
      : "—";

  return (
    <div
      className="hidden items-center gap-2 rounded-md bg-surface-subtle px-3 py-1.5 lg:inline-flex"
      title={t("topbar.balance_tooltip")}
    >
      <span className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-ink-subtle">
        {t("topbar.balance_label")}
      </span>
      <span className="font-mono text-caption font-medium tabular-nums text-ink">
        € {balance}
      </span>
    </div>
  );
}

// =================================================================
// Icon button (bell + help)
// =================================================================

function IconButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Bell;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-9 w-9 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface-subtle hover:text-ink focus:outline-none focus-visible:shadow-focus"
    >
      <Icon className="h-4 w-4 stroke-[1.75]" aria-hidden="true" />
    </button>
  );
}

// =================================================================
// Avatar dropdown menu
// =================================================================

function AvatarMenu() {
  const { t } = useTranslation();
  const { user, organization } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const logoutMutation = useMutation({
    mutationFn: () => api.logout(),
    onSettled: async () => {
      // QC-LOGOUT-1 (28-Oct-2026): drop every cached query of the account that just left (wallet, messages,
      // contacts...), so a different account signing in on the same tab never sees the previous one's data
      // for up to gcTime. Only /me stays, invalidated as before (it drives the redirect).
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== meQueryKey[0] });
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      navigate("/login", { replace: true });
    },
  });

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const initial = (user?.full_name ?? user?.email ?? "?")
    .charAt(0)
    .toUpperCase();

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "flex h-9 items-center gap-1.5 rounded-pill pl-0.5 pr-2 transition-colors",
          "focus:outline-none focus-visible:shadow-focus",
          open ? "bg-surface-subtle" : "hover:bg-surface-subtle",
        )}
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-500 text-caption font-semibold text-white">
          {initial}
        </div>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 stroke-[2] text-ink-muted transition-transform",
            open && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-11 z-20 w-64 origin-top-right overflow-hidden rounded-lg border border-border bg-surface-card shadow-lg animate-scale-in"
        >
          {/* Header — org + email */}
          <div className="border-b border-border px-4 py-3">
            {organization ? (
              <div className="text-caption font-semibold text-ink">
                {organization.name}
              </div>
            ) : null}
            {user ? (
              <div className="truncate text-[12px] text-ink-muted">
                {user.email}
              </div>
            ) : null}
          </div>

          {/* Action: sign out */}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logoutMutation.mutate();
            }}
            disabled={logoutMutation.isPending}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-caption text-ink transition-colors hover:bg-surface-subtle disabled:opacity-60"
          >
            <LogOut
              className="h-3.5 w-3.5 stroke-[1.75] text-ink-muted"
              aria-hidden="true"
            />
            <span>{t("topbar.sign_out")}</span>
          </button>
        </div>
      )}
    </div>
  );
}
